import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AlertTriangle, CalendarRange, Plus } from 'lucide-react';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import {
  StandardTable,
  StandardTableBody,
  StandardTableCell,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableRow,
  EmptyTableState,
  LoadingTableState,
} from '../../../components/StandardTable';
import AdminModal from '../../components/AdminModal';
import { CYCLE_ELECTION_TYPES, defaultCycleWindow } from '../../../constants/elections';
import { formatDate } from '../../lib/format';

export interface ElectionCycle {
  id: string;
  state: string;
  election_year: number;
  election_type: string;
  name: string | null;
  notes: string | null;
  cycle_start_date: string | null;
  cycle_end_date: string | null;
  candidate_count: number;
  contest_count: number;
  milestones: Array<{ id: string }> | null;
}

const inputClasses =
  'w-full rounded-lg border border-border-light bg-white px-3 py-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary';
const labelClasses = 'mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary';
const filterSelectClasses =
  'h-9 rounded-lg border border-border-light bg-white px-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary';

const COLUMN_COUNT = 7;

const nextEvenYear = () => {
  const year = new Date().getFullYear();
  return year % 2 === 0 ? year : year + 1;
};

const emptyCycleForm = () => {
  const year = nextEvenYear();
  const window = defaultCycleWindow(year);
  return {
    election_year: String(year),
    election_type: 'regular',
    name: '',
    cycle_start_date: window.start,
    cycle_end_date: window.end,
  };
};

const apiError = (error: any, fallback: string) =>
  error?.response?.data?.error ?? error?.response?.data?.message ?? fallback;

/**
 * Election Cycles list — one row per configured cycle (election_calendars).
 * The cycle window prefills to Colorado's statutory campaign-finance window
 * and stays editable (the calendar row is authoritative).
 */
const CyclesView: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [yearFilter, setYearFilter] = useState('all');
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(emptyCycleForm);

  const cycles = useQuery({
    queryKey: ['admin-elections', 'cycles'],
    queryFn: async () => {
      const response = await api.get('/election-calendars');
      return (response.data.data ?? []) as ElectionCycle[];
    },
  });

  const createMutation = useMutation({
    mutationFn: () =>
      api.post('/election-calendars', {
        election_year: parseInt(form.election_year, 10),
        election_type: form.election_type,
        name: form.name.trim() || undefined,
        cycle_start_date: form.cycle_start_date,
        cycle_end_date: form.cycle_end_date,
      }),
    onSuccess: (response) => {
      toast.success('Election cycle saved');
      setAddOpen(false);
      setForm(emptyCycleForm());
      queryClient.invalidateQueries({ queryKey: ['admin-elections', 'cycles'] });
      const created = response.data.data as ElectionCycle | undefined;
      if (created?.id) navigate(`/admin-next/elections/cycles/${created.id}`);
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to save cycle')),
  });

  const years = [...new Set((cycles.data ?? []).map((cycle) => cycle.election_year))].sort((a, b) => b - a);
  const rows = (cycles.data ?? []).filter(
    (cycle) => yearFilter === 'all' || String(cycle.election_year) === yearFilter
  );

  const setYear = (value: string) => {
    const year = parseInt(value, 10);
    setForm((current) => {
      if (!Number.isFinite(year) || String(year).length !== 4) {
        return { ...current, election_year: value };
      }
      const window = defaultCycleWindow(year);
      return { ...current, election_year: value, cycle_start_date: window.start, cycle_end_date: window.end };
    });
  };

  const saveDisabled =
    !/^\d{4}$/.test(form.election_year.trim()) || form.cycle_start_date === '' || form.cycle_end_date === '';

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button variant="primary" size="sm" icon={Plus} onClick={() => { setForm(emptyCycleForm()); setAddOpen(true); }}>
          Add Cycle
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={yearFilter}
          onChange={(event) => setYearFilter(event.target.value)}
          aria-label="Filter by year"
          className={filterSelectClasses}
        >
          <option value="all">All years</option>
          {years.map((year) => (
            <option key={year} value={String(year)}>{year}</option>
          ))}
        </select>
      </div>

      {cycles.isError ? (
        <div className="card flex items-center gap-3 border-l-4 border-error-500 p-4">
          <AlertTriangle className="h-5 w-5 shrink-0 text-error-600 dark:text-error-400" />
          <p className="flex-1 text-sm text-text-secondary dark:text-text-dark-secondary">
            Couldn't load election cycles.
          </p>
          <Button variant="outline" size="sm" onClick={() => cycles.refetch()}>Retry</Button>
        </div>
      ) : (
        <StandardTable horizontalScroll loading={cycles.isFetching && !cycles.isLoading}>
          <StandardTableHeader>
            <StandardTableRow>
              <StandardTableHeaderCell>Year</StandardTableHeaderCell>
              <StandardTableHeaderCell>Name</StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="md">Type</StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="lg">Cycle Window</StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="lg">Milestones</StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="xl">Contests</StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="xl">Candidates</StandardTableHeaderCell>
            </StandardTableRow>
          </StandardTableHeader>
          <StandardTableBody>
            {cycles.isLoading ? (
              <LoadingTableState colSpan={COLUMN_COUNT} />
            ) : rows.length === 0 ? (
              <EmptyTableState
                colSpan={COLUMN_COUNT}
                message="No election cycles configured"
                icon={<CalendarRange className="h-8 w-8" />}
              />
            ) : (
              rows.map((cycle) => (
                <StandardTableRow key={cycle.id} onClick={() => navigate(`/admin-next/elections/cycles/${cycle.id}`)}>
                  <StandardTableCell>
                    <span className="font-medium text-text-primary dark:text-text-dark-primary">
                      {cycle.election_year}
                    </span>
                  </StandardTableCell>
                  <StandardTableCell>{cycle.name || '—'}</StandardTableCell>
                  <StandardTableCell responsiveHide="md">{cycle.election_type}</StandardTableCell>
                  <StandardTableCell responsiveHide="lg">
                    {cycle.cycle_start_date && cycle.cycle_end_date
                      ? `${formatDate(cycle.cycle_start_date)} – ${formatDate(cycle.cycle_end_date)}`
                      : '—'}
                  </StandardTableCell>
                  <StandardTableCell responsiveHide="lg">{cycle.milestones?.length ?? 0}</StandardTableCell>
                  <StandardTableCell responsiveHide="xl">{cycle.contest_count}</StandardTableCell>
                  <StandardTableCell responsiveHide="xl">{cycle.candidate_count}</StandardTableCell>
                </StandardTableRow>
              ))
            )}
          </StandardTableBody>
        </StandardTable>
      )}

      <AdminModal
        isOpen={addOpen}
        title="Add Election Cycle"
        onClose={() => setAddOpen(false)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button variant="primary" size="sm" disabled={saveDisabled}
              loading={createMutation.isLoading} onClick={() => createMutation.mutate()}>
              Save Cycle
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="cycle-year" className={labelClasses}>Election Year *</label>
              <input id="cycle-year" type="number" min={2020} max={2040} value={form.election_year}
                onChange={(event) => setYear(event.target.value)} className={inputClasses} />
            </div>
            <div>
              <label htmlFor="cycle-type" className={labelClasses}>Type</label>
              <select id="cycle-type" value={form.election_type}
                onChange={(event) => setForm((current) => ({ ...current, election_type: event.target.value }))}
                className={inputClasses}>
                {CYCLE_ELECTION_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label htmlFor="cycle-name" className={labelClasses}>Display Name</label>
            <input id="cycle-name" type="text" value={form.name}
              placeholder={`${form.election_year} CO ${form.election_type.charAt(0).toUpperCase()}${form.election_type.slice(1)}`}
              onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
              className={inputClasses} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="cycle-start" className={labelClasses}>Cycle Window Start *</label>
              <input id="cycle-start" type="date" value={form.cycle_start_date}
                onChange={(event) => setForm((current) => ({ ...current, cycle_start_date: event.target.value }))}
                className={inputClasses} />
            </div>
            <div>
              <label htmlFor="cycle-end" className={labelClasses}>Cycle Window End *</label>
              <input id="cycle-end" type="date" value={form.cycle_end_date}
                onChange={(event) => setForm((current) => ({ ...current, cycle_end_date: event.target.value }))}
                className={inputClasses} />
            </div>
          </div>
          <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
            The window prefills to Colorado's statutory campaign-finance cycle (day after the prior
            general election through this year's general election) and can be overridden.
          </p>
        </div>
      </AdminModal>
    </div>
  );
};

export default CyclesView;
